import { Link } from "@inertiajs/react";
import { PhotoIcon, SpeakerWaveIcon, ArrowRightIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { cn } from "@lib/utils";
import { threadUrl, type InertiaProps } from "@shared";

// The site row consumes the same serialized shape as the /projects cards
// (to_mini_json), which carries the brainstorm/website thread homes.
export type SiteMini =
  InertiaProps.paths["/projects"]["get"]["responses"]["200"]["content"]["application/json"]["projects"][number];

// ── Ad row data ───────────────────────────────────────────────────────────
// Serialized by ProjectsController#ad_mini_json for the "Jump back in" hub.
export type AdStatus = "draft" | "active" | "paused" | "completed";

export interface AdMini {
  id: number;
  uuid: string;
  name: string | null;
  status: AdStatus;
  updated_at: string | null;
  preview_screenshot_url: string | null;
  headlines: { text: string | null }[];
  project: { uuid: string; name: string };
}

// Brainstorm and website each have a stable thread home; fall back to the
// legacy step URL (which 302s to the same thread) for older projects.
function brainstormHref(project: SiteMini): string {
  return project.brainstorm_thread_id
    ? threadUrl(project.brainstorm_thread_id)
    : `/projects/${project.uuid}/brainstorm`;
}

function websiteHref(project: SiteMini): string {
  return project.website_thread_id
    ? threadUrl(project.website_thread_id)
    : `/projects/${project.uuid}/website/build`;
}

// Mirrors the primary action button: an editable website opens the website
// thread, otherwise we drop the user straight into brainstorm.
function projectPrimaryHref(project: SiteMini): string {
  return project.available_actions.includes("edit_website")
    ? websiteHref(project)
    : brainstormHref(project);
}

const BADGE_CLASS =
  "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide leading-4 shrink-0";

const SITE_BADGE: Record<SiteMini["status"], string> = {
  live: "bg-success-100 text-success-700",
  paused: "bg-accent-yellow-100 text-accent-yellow-700",
  draft: "bg-neutral-100 text-base-600",
};
const SITE_LABEL: Record<SiteMini["status"], string> = {
  live: "Live",
  paused: "Paused",
  draft: "Draft",
};

const AD_BADGE: Record<AdStatus, string> = {
  active: "bg-success-100 text-success-700",
  paused: "bg-accent-yellow-100 text-accent-yellow-700",
  draft: "bg-neutral-100 text-base-600",
  completed: "bg-neutral-100 text-base-600",
};
const AD_LABEL: Record<AdStatus, string> = {
  active: "Active",
  paused: "Paused",
  draft: "Draft",
  completed: "Completed",
};

const DOT_COLOR: Record<SiteMini["status"], string> = {
  live: "bg-success-500",
  paused: "bg-accent-yellow-500",
  draft: "bg-neutral-300",
};

function StatusBadge({ styles, label }: { styles: string; label: string }) {
  return <span className={cn(BADGE_CLASS, styles)}>{label}</span>;
}

// Dark "Open project" / "Open ad" pill with a purple arrow. Sits above the
// row's stretched-link overlay via z-10.
function OpenPill({ href, label }: { href: string; label: string }) {
  return (
    <Link
      href={href}
      className="relative z-10 inline-flex items-center gap-1.5 rounded-lg bg-neutral-900 px-3.5 py-2.5 text-sm font-medium text-white shrink-0 transition-colors hover:bg-neutral-800"
    >
      <ArrowRightIcon className="w-4 h-4 text-primary-400" />
      {label}
    </Link>
  );
}

const ROW_CLASS =
  "relative flex items-center gap-4 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm transition-all hover:border-neutral-300 hover:shadow";

function SiteThumbnail({ project }: { project: SiteMini }) {
  return (
    <div className="w-16 h-16 shrink-0 overflow-hidden rounded-xl bg-neutral-50 flex items-center justify-center">
      {project.preview_screenshot_url ? (
        <img
          src={project.preview_screenshot_url}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover object-top"
        />
      ) : (
        <PhotoIcon className="w-6 h-6 text-base-400" />
      )}
    </div>
  );
}

export function SiteRow({ project }: { project: SiteMini }) {
  const href = projectPrimaryHref(project);
  return (
    <div className={ROW_CLASS}>
      <Link href={href} aria-label={`Open ${project.name}`} className="absolute inset-0 rounded-2xl" />
      <SiteThumbnail project={project} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-serif text-lg font-semibold text-base-600 truncate">{project.name}</h4>
          <StatusBadge styles={SITE_BADGE[project.status]} label={SITE_LABEL[project.status]} />
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <span
            className={cn(
              "w-1.5 h-1.5 rounded-full shrink-0",
              project.domain ? DOT_COLOR[project.status] : "bg-neutral-300"
            )}
          />
          {project.domain ? (
            <a
              href={`https://${project.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="relative z-10 truncate font-mono text-xs text-primary-500"
            >
              {project.domain}
            </a>
          ) : (
            <span className="font-mono text-xs text-neutral-500">No site connected</span>
          )}
        </div>
      </div>
      <OpenPill href={href} label="Open project" />
    </div>
  );
}

export function AdRow({ ad }: { ad: AdMini }) {
  const href = `/campaigns/${ad.uuid}/content`;
  const title =
    ad.name?.trim() ||
    ad.headlines
      .map((h) => h.text)
      .filter(Boolean)
      .join(" | ") ||
    "Untitled ad";
  return (
    <div className={ROW_CLASS}>
      <Link href={href} aria-label={`Open ${title}`} className="absolute inset-0 rounded-2xl" />
      <div className="w-16 h-16 shrink-0 overflow-hidden rounded-xl bg-neutral-100 flex items-center justify-center">
        {ad.preview_screenshot_url ? (
          <img
            src={ad.preview_screenshot_url}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <SpeakerWaveIcon className="w-6 h-6 text-base-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-sans text-base font-semibold text-primary-600 truncate">{title}</h4>
          <StatusBadge styles={AD_BADGE[ad.status]} label={AD_LABEL[ad.status]} />
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <TableCellsIcon className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
          <span className="truncate font-mono text-xs text-neutral-500">in {ad.project.name}</span>
        </div>
      </div>
      <OpenPill href={href} label="Open ad" />
    </div>
  );
}
